import { t, tc } from "./i18n"
import type { OrderDetail, ProductProfit, ReturnReason, ReturnWay } from "./medusa"

/**
 * "Return received" on a site order, as plain functions the panel draws from. The backend
 * (lib/hq/returns.ts in the Medusa app) checks the same things again before anything moves.
 *
 * The owners' rules (2026-10-08): the way it came back is agreed in a DM, so HQ only notes it; every
 * unit is put back on sale or not, chosen each time (no default); the outcome is a refund or an exchange.
 */

// Labels are getters: read when drawn, in the language chosen then, never at import.
export const REASONS: Array<{ key: ReturnReason; label: string }> = [
  { key: "size", get label() { return t("Size didn't fit") } },
  { key: "changed_mind", get label() { return t("Changed their mind") } },
  { key: "faulty", get label() { return tc("return", "Faulty") } },
  { key: "wrong_item", get label() { return t("Wrong item sent") } },
  { key: "other", get label() { return tc("return", "Other") } },
]

export const WAYS: Array<{ key: ReturnWay; label: string }> = [
  { key: "box_now", get label() { return tc("way", "BOX NOW") } },
  { key: "meetup", get label() { return tc("way", "Meet-up") } },
  { key: "courier", get label() { return tc("way", "Courier") } },
  { key: "other", get label() { return tc("way", "Other") } },
]

export const reasonLabel = (key: string) => REASONS.find((r) => r.key === key)?.label ?? key
export const wayLabel = (key: string | null) => (key ? WAYS.find((w) => w.key === key)?.label ?? key : null)

export type Line = NonNullable<OrderDetail["items"]>[number]

/** The lines that can still come back: sent, and not returned already. */
export const returnable = (order: OrderDetail) => order.items.filter((item) => (item.returnable ?? 0) > 0)

const cents = (eur: number) => Math.round(eur * 100)

/** Picked units per line id; `restock` per line id (true / false, or undefined when not chosen yet). */
export type Draft = {
  picked: Record<string, number>
  restock: Record<string, boolean | undefined>
  reason: ReturnReason | null
  outcome: "refund" | "exchange"
  refund: string
  exchange: { variant_id: string | null; quantity: number }
  refundDifference: boolean
}

export const isExtra = (line: Line) => !!line.extra_for

/** What the picked units were paid (after any discount code). */
export function returnedValue(order: OrderDetail, picked: Record<string, number>): number {
  return order.items.reduce((sum, line) => sum + cents(line.paid_unit_price ?? line.unit_price) * (picked[line.id] ?? 0), 0) / 100
}

/**
 * The refund to suggest: what the picked units were paid, plus the delivery the order charged when
 * everything that was sent comes back (the policies page: a withdrawal gets "the amount you paid,
 * including the original delivery"). Never more than the order still allows back.
 */
export function suggestedRefund(order: OrderDetail, picked: Record<string, number>): number {
  let total = cents(returnedValue(order, picked))
  const goods = returnable(order).filter((line) => !isExtra(line))
  if (goods.length && goods.every((line) => (picked[line.id] ?? 0) >= (line.returnable ?? 0))) total += cents(order.delivery_charged ?? 0)
  return Math.min(total, cents(order.payment.refundable)) / 100
}

/** The products an exchange can send: those in the shop with a size that can be sold now. */
export function exchangeChoices(profit: ProductProfit | undefined) {
  return (profit?.products ?? [])
    .filter((product) => product.status === "published" && product.sizes.some((size) => size.variant_id))
    .map((product) => ({
      id: product.id ?? product.handle,
      title: product.title,
      price: product.price,
      sizes: product.sizes.filter((size): size is typeof size & { variant_id: string } => !!size.variant_id),
    }))
}

/** The product a returned size belongs to, to start the exchange on the same product. */
export function productOfVariant(profit: ProductProfit | undefined, variantId: string | null | undefined) {
  if (!variantId) return null
  return exchangeChoices(profit).find((product) => product.sizes.some((size) => size.variant_id === variantId)) ?? null
}

/** What still stops Save, in words; empty when the return can be recorded. */
export function problems(order: OrderDetail, draft: Draft, refundable: number, exchangeAvailable: number | null): string[] {
  const out: string[] = []
  const lines = returnable(order).filter((line) => (draft.picked[line.id] ?? 0) > 0)
  if (!lines.length) out.push(t("Choose what came back."))
  if (lines.some((line) => !isExtra(line) && draft.restock[line.id] === undefined)) out.push(t("Choose for each item whether it goes back on sale."))
  if (!draft.reason) out.push(t("Choose why it came back."))
  if (draft.outcome === "refund") {
    const value = Number(draft.refund.replace(",", "."))
    if (!Number.isFinite(value) || value <= 0) out.push(t("Enter the amount to refund."))
    else if (cents(value) > cents(refundable)) out.push(t("More than this order allows back."))
  } else {
    if (!draft.exchange.variant_id) out.push(t("Choose what goes out in the exchange."))
    else if (exchangeAvailable != null && draft.exchange.quantity > exchangeAvailable) out.push(t("Not enough of that size in stock."))
  }
  return out
}

/** One per opening of the form, so a retried Save records the return once. */
export function newReturnRequestId(): string {
  const random = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `ret-${random}`.slice(0, 64)
}
